import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import memoize from 'memoize-one';
import YearPanel from './panel/YearPanel';
import MonthPanel from './panel/MonthPanel';
import DatePanel from './panel/DatePanel';
import TimePanel from '../timePicker/TimePanel';
import TypeSelect from './TypeSelect';

/**
 * @ignore
 * */
export default class Calendar extends React.PureComponent {
  static propTypes = {
    selectType: PropTypes.string,
    rangeSeparator: PropTypes.string,
  };

  static defaultProps = {
    value: moment(),
    selectType: 'date',
    prefixCls: 'ne-calendar',
  };

  static getDerivedStateFromProps(props, state) {
    let newState = null;
    if (props.panelDate !== state.prevPropsPanelDate) {
      newState = {
        panelDate: props.panelDate,
        prevPropsPanelDate: props.panelDate,
      };
    }
    if (props.panelType !== state.prevPropsPanelType) {
      newState = newState || {};
      newState.panelType = props.panelType;
      newState.prevPropsPanelType = props.panelType;
    }
    return newState;
  }

  constructor(props) {
    super(props);
    const { value, panelDate, selectType, panelType } = props;
    const initialPanelDate = moment(panelDate || value);

    this.state = {
      panelDate: initialPanelDate,
      panelType: panelType || selectType,
    };
  }

  handlePanelDateChange = value => {
    const { onPanelDateChange } = this.props;
    this.setState({
      panelDate: value,
    });
    onPanelDateChange && onPanelDateChange(value);
  };

  handlePanelTypeChange = type => {
    this.setState({
      panelType: type,
    });
    // const { onPanelTypeChange } = this.props;
    // onPanelTypeChange && onPanelTypeChange(type);
  };

  handleTimeChange = value => {
    const { onTimeChange, rangeType } = this.props;
    onTimeChange(value, rangeType);
    this.handlePanelDateChange(value);
  };

  conditions = memoize((date, type, disabledTime) =>
    disabledTime ? disabledTime(moment(date), type) : {},
  );

  disabledHoursCondition = (selectedDate, hour) => {
    const { rangeType, value, panelDate, disabledTime } = this.props;
    const timeConditions = this.conditions(panelDate, rangeType, disabledTime);
    if (rangeType === 'start') {
      return (
        (value[0] &&
          value[1] &&
          moment(value[0])
            .hour(hour)
            .isAfter(value[1], 'hour')) ||
        (timeConditions.disabledHours && timeConditions.disabledHours(selectedDate, hour))
      );
    }
    if (rangeType === 'end') {
      return (
        (value[0] && value[1] && value[0].isAfter(moment(value[1]).hour(hour), 'hour')) ||
        (timeConditions.disabledHours && timeConditions.disabledHours(selectedDate, hour))
      );
    }
    return timeConditions.disabledHours && timeConditions.disabledHours(selectedDate, hour);
  };

  disabledMinutesCondition = (selectedDate, selectedHour, minute) => {
    const { rangeType, value, panelDate, disabledTime } = this.props;
    const timeConditions = this.conditions(panelDate, rangeType, disabledTime);
    if (rangeType === 'start') {
      return (
        (value[0] &&
          value[1] &&
          moment(value[0])
            .minute(minute)
            .isAfter(value[1], 'minute')) ||
        (timeConditions.disabledMinutes && timeConditions.disabledMinutes(selectedDate, selectedHour, minute))
      );
    }
    if (rangeType === 'end') {
      return (
        (value[0] && value[1] && value[0].isAfter(moment(value[1]).minute(minute), 'minute')) ||
        (timeConditions.disabledMinutes && timeConditions.disabledMinutes(selectedDate, selectedHour, minute))
      );
    }
    return timeConditions.disabledMinutes && timeConditions.disabledMinutes(selectedDate, selectedHour, minute);
  };

  disabledSecondsCondition = (selectedDate, selectedHour, selectedMinute, second) => {
    const { rangeType, value, panelDate, disabledTime } = this.props;
    const timeConditions = this.conditions(panelDate, rangeType, disabledTime);
    if (rangeType === 'start') {
      return (
        (value[0] &&
          value[1] &&
          moment(value[0])
            .second(second)
            .isSameOrAfter(value[1], 'second')) ||
        (timeConditions.disabledSeconds &&
          timeConditions.disabledSeconds(selectedDate, selectedHour, selectedMinute, second))
      );
    }
    if (rangeType === 'end') {
      return (
        (value[0] &&
          value[1] &&
          value[0].isSameOrAfter(moment(value[1]).second(second), 'second')) ||
        (timeConditions.disabledSeconds &&
          timeConditions.disabledSeconds(selectedDate, selectedHour, selectedMinute, second))
      );
    }
    return (
      timeConditions.disabledSeconds &&
      timeConditions.disabledSeconds(selectedDate, selectedHour, selectedMinute, second)
    );
  };

  render() {
    const {
      onValueChange,
      value,
      selectType,
      prefixCls,
      disabledDate,
      disabledMonth,
      disabledYear,
      disabledTime,
      rangeType,
      startPanelDate,
      endPanelDate,
    } = this.props;
    const { panelDate, panelType } = this.state;
    const panelCommonProps = {
      value,
      panelDate,
      onValueChange,
      selectType,
      rangeType,
      onPanelTypeChange: this.handlePanelTypeChange,
      onPanelDateChange: this.handlePanelDateChange,
    };
    let timeValue = value;
    if (rangeType === 'start') {
      [timeValue] = value;
    } else if (rangeType === 'end') {
      [, timeValue] = value;
    }
    return (
      <table className={`${prefixCls} ${prefixCls}-${panelType}`}>
        <caption>
          <TypeSelect
            startPanelDate={startPanelDate} // forceUpdate
            endPanelDate={endPanelDate} // forceUpdate
            prefixCls={prefixCls}
            rangeType={rangeType}
            disabledYear={disabledYear}
            disabledMonth={disabledMonth}
            disabledDate={disabledDate}
            panelType={panelType}
            panelDate={panelDate}
            onPanelTypeChange={this.handlePanelTypeChange}
            onPanelDateChange={this.handlePanelDateChange} />
        </caption>
        {panelType === 'date' && (
          <DatePanel {...panelCommonProps} prefixCls={prefixCls} disabledDate={disabledDate} />
        )}
        {panelType === 'month' && (
          <MonthPanel {...panelCommonProps} disabledMonth={disabledMonth} prefixCls={prefixCls} />
        )}
        {panelType === 'year' && (
          <YearPanel {...panelCommonProps} disabledYear={disabledYear} prefixCls={prefixCls} />
        )}
        {panelType === 'time' && (
          <TimePanel
            {...panelCommonProps}
            rangeValue={value} // forceUpdate
            value={timeValue}
            onValueChange={this.handleTimeChange}
            disabledTime={disabledTime}
            disabledHours={this.disabledHoursCondition}
            disabledMinutes={this.disabledMinutesCondition}
            disabledSeconds={this.disabledSecondsCondition} />
        )}
      </table>
    );
  }
}



// WEBPACK FOOTER //
// ./src/datePicker/Calendar.jsx